import { useEffect, useMemo, useState } from 'react'
import { Abrufausfall, useAbruf, useHintergrundabruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { meldeErfolg } from './Erfolgsstelle'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { ohneStatistikkonten } from './Festkonten'
import type { Auswahleintrag } from './Auswahlfeld'
import { Kontofeld } from './Kontofeld'
import { Blattabschnitt } from './Stammdaten'
import { unterschiede, vorgaengerin } from './Handbuchvergleich'
import { Nachfrage } from './Nachfrage'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { Zahlenfeld } from './Zahlenfeld'
import { anwendername, periodenname, satz, t, zahlenschema, anzahlwort } from './sprache'
import type { Handbuchausgabe, Icpaargrenze, Regelparameter } from './typen'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'

/** Ersatz, wenn eine Auswahlliste im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_WAHL: Auswahleintrag[] = []

/**
 * Das Konzernhandbuch — der Pflegebildschirm (2.11 Abschnitt 9, R779).
 *
 * **Der Befund vom 2026-09-26:** Der erste Konzernabschluß brach ab, weil es
 * kein Regelwerk gab. Es kam mit dem Seed des Demokonzerns und ging mit ihm,
 * und anlegen ließ es sich nirgends — wer eine neue Anlage einrichtet, kommt
 * bis zum ersten Abschluß und dann nicht weiter.
 *
 * **Hier und nicht in der Stammdatenpflege**, auf Entscheidung des
 * Auftraggebers: *„Die Pflege der relevanten Felder soll zusammenhängend und
 * für den Anwender übersichtlich und nachvollziehbar erfolgen."* Die Parameter
 * liegen in der Datenbank als ein JSON-Feld, und die Stammdatenpflege kennt
 * kein JSON-Feld. Vor allem aber gehören Angaben, die nur zusammen einen Sinn
 * ergeben, auf einen Bildschirm und nicht zwischen Kontenrahmen und
 * Bewegungsarten.
 *
 * **Drei Abschnitte statt einer Wand aus Feldern** (R515): die Ausgabe selbst,
 * das Runden, und die Konten, auf die die Konsolidierung bucht. Zugeklappt
 * trägt jeder Abschnitt seine Auskunft.
 *
 * **Vorerst nur HGB** (R779): Was es nicht gibt, steht nicht da. IFRS kommt
 * dazu, wenn der Prototyp steht — dann als zweites Kapitel neben diesem.
 *
 * **Rundungsart und Differenzausgleich sind keine Angaben mehr** (2.13
 * Abschnitt 17, R1397): CoKo rundet fest kaufmännisch, und jeder Rest geht
 * auf das Konto ab Werk „Rundungsdifferenzen“ (R1424). Ebenso die
 * Währungsumrechnungsrücklage, ein Konto ab Werk (R1426).
 */
export function Konzernhandbuch() {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse1, rahmen: spaltenrahmen1 } =
    useSpaltenmasse<HTMLTableElement>('konzernhandbuch:konzernhandbuch:1')

  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse2, rahmen: spaltenrahmen2 } =
    useSpaltenmasse<HTMLTableElement>('konzernhandbuch:konzernhandbuch:2')
  // Was der Angemeldete nicht darf, ist nicht da (8.1 Abschnitt 7, R1180).
  const darf = useDarf()

  const [gewaehlt, setGewaehlt] = useState<number | null>(null)
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [loeschen, setLoeschen] = useState<Handbuchausgabe | null>(null)

  // Die Ausgaben sind der Bestand der Seite — ein Hauptabruf (R1211). Kamen
  // sie nicht, steht da „Ließ sich nicht laden" und nicht „keine Ausgabe".
  const ausgabenabruf = useAbruf(
    () =>
      api.konzernhandbuch().then((alle) => {
        setGewaehlt((bisher) => bisher ?? alle[0]?.ausgabe ?? null)
        return alle
      }),
    [],
  )
  const ausgaben = ausgabenabruf.wert

  // Die Konten kommen aus dem Kontenrahmen: Ein Buchungskonto wird ausgesucht
  // und nicht getippt (R327) — ein Tippfehler zeigte sonst auf ein Konto, das
  // es nicht gibt, und der Abschluß bräche erst beim Rechnen ab.
  // Eine Nachschlageliste, im Hintergrund (7.2 Abschnitt 30, R1007, R1211).
  // Ohne Statistikkonten: Die Konsolidierung bebucht sie nie (R1734).
  const konten = useHintergrundabruf(
    () => api.stammdatenliste('konto').then((liste) => ohneStatistikkonten(liste.eintraege, (e) => e.schluessel).map(ausStammdatum)),
    [],
    KEINE_WAHL,
  )

  // Und die Gesellschaften: Eine Schwelle je Paarbeziehung nennt zwei von
  // ihnen, und sie werden ausgesucht und nicht getippt (R327). Eine
  // Auswahlliste, im Hintergrund (R1211).
  const einheiten = useHintergrundabruf(
    () => api.stammdatenliste('einheit').then((liste) => liste.eintraege.map(ausStammdatum)),
    [],
    KEINE_WAHL,
  )

  const offen = useMemo(
    () => ausgaben?.find((a) => a.ausgabe === gewaehlt) ?? null,
    [ausgaben, gewaehlt],
  )

  // Verglichen wird gegen die nächstältere Ausgabe (R780). Die älteste hat
  // keine Vorgängerin -- ein Vergleich gegen nichts wäre eine Tabelle, in
  // der jede Zeile steht.
  const vorher = useMemo(
    () => (offen && ausgaben ? vorgaengerin(ausgaben, offen.ausgabe) : null),
    [ausgaben, offen],
  )

  const geaendert = useMemo(
    () => (offen && vorher ? unterschiede(vorher, offen) : []),
    [offen, vorher],
  )

  // **Der Entwurf fängt bei dem an, was gespeichert ist.** Ein Wechsel der
  // Ausgabe wirft Getipptes weg — was halb eingetragen unter einer anderen
  // Nummer stehenbliebe, wäre eine Falle.
  useEffect(() => {
    setEntwurf(offen ? ausAusgabe(offen) : null)
    setMeldung(null)
  }, [offen])

  async function tue(was: () => Promise<Handbuchausgabe[]>, hinweis: string) {
    setLaeuft(true)
    setMeldung(null)

    try {
      const alle = await was()
      ausgabenabruf.setze(alle)
      setLoeschen(null)
      // Was gelang, sagt das Kästchen oben rechts (7.2 Abschnitt 34, R1130).
      meldeErfolg(hinweis)
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  function neueAusgabe() {
    const naechste = Math.max(0, ...(ausgaben ?? []).map((a) => a.ausgabe)) + 1
    setGewaehlt(null)
    setEntwurf({
      ausgabe: naechste,
      name: t('handbuch.neu.name', { nummer: String(naechste) }),
      gueltigVon: null,
      gueltigBis: null,
      parameter: { ...VORGABE },
    })
    setMeldung(null)
  }

  if (ausgaben === null) {
    return (
      <section className="konzernhandbuch">
        <h1>{t('bereich.abschluss.konzernhandbuch')}</h1>
        {ausgabenabruf.ausgefallen && <Abrufausfall abruf={ausgabenabruf} />}
      </section>
    )
  }

  const gesperrt = offen !== null && !offen.aenderbar

  return (
    <section className="konzernhandbuch">
      <h1>{t('bereich.abschluss.konzernhandbuch')}</h1>

      {/* ---- Die Ausgaben, jüngste zuerst ------------------------------ */}
      <div className="tabellenrahmen">
        <table ref={spaltenrahmen1} className="ziehbaretabelle">
          <Spaltenmassleiste masse={spaltenmasse1} />
          <thead>
            <tr>
              <th>{t('handbuch.spalte.ausgabe')}</th>
              <th>{t('handbuch.spalte.name')}</th>
              <th>{t('handbuch.spalte.gueltig')}</th>
              <th>{t('handbuch.spalte.abschluesse')}</th>
              {/* Mit dem Anwender, wie jedes Protokoll (7.2 Abschnitt 42, R1270). */}
              <th>{t('protokoll.spalte.anwender')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {ausgaben.length === 0 && (
              <tr>
                <td colSpan={6} className="leerhinweis">
                  {t('handbuch.leer')}
                </td>
              </tr>
            )}
            {ausgaben.map((a) => (
              <tr
                key={a.ausgabe}
                aria-current={a.ausgabe === gewaehlt ? true : undefined}
                className={a.ausgabe === gewaehlt ? 'gewaehlt' : undefined}
                onClick={() => setGewaehlt(a.ausgabe)}
              >
                <td className="nummer">{a.ausgabe}</td>
                <td>{a.name}</td>
                <td>{geltung(a)}</td>
                <td className="anzahl">{a.abschluesse}</td>
                <td>{a.anwender ? anwendername(a.anwender) : '—'}</td>
                <td className="handgriff">
                  {a.aenderbar ? (
                    darf('konzernhandbuchEntfernen') && (
                      <button
                        type="button"
                        className="schmal gefahr"
                        disabled={laeuft}
                        onClick={(e) => {
                          e.stopPropagation()
                          setLoeschen(a)
                        }}
                      >
                        {t('handbuch.entfernen')}
                      </button>
                    )
                  ) : (
                    <span className="gesperrt" title={a.sperre ? satz(a.sperre) : undefined}>
                      {t('handbuch.gesperrt')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {darf('konzernhandbuchPflegen') && (
        <div className="knopfreihe">
          <button type="button" onClick={neueAusgabe} disabled={laeuft}>
            {t('handbuch.neu')}
          </button>
        </div>
      )}

      {/*
        **Die Sperre steht über den Feldern und nicht hinter dem Knopf.** Wer
        erst beim Speichern erfährt, daß er nichts ändern darf, hat umsonst
        getippt — ein freigegebener Abschluß trägt seine Regelfassung (R98).
      */}
      {gesperrt && offen?.sperre && <p className="hinweis gesperrt">{satz(offen.sperre)}</p>}

      {entwurf && (
        <Pflege
          entwurf={entwurf}
          konten={konten}
          einheiten={einheiten}
          // Ohne Recht zum Pflegen steht die Ausgabe zum Lesen da (R1180).
          gesperrt={gesperrt || !darf('konzernhandbuchPflegen')}
          laeuft={laeuft}
          aufAenderung={setEntwurf}
          aufSpeichern={() =>
            void tue(
              () =>
                api.konzernhandbuchPflegen(
                  entwurf.ausgabe,
                  entwurf.name,
                  entwurf.gueltigVon ?? 0,
                  entwurf.gueltigBis,
                  entwurf.parameter,
                ),
              t('erfolg.handbuch.gespeichert'),
            ).then(() => setGewaehlt(entwurf.ausgabe))
          }
        />
      )}

      {/*
        **Der Ausgabenvergleich** (2.11 Abschnitt 9.6, R780) — der letzte der
        vier Punkte, die der aufgelöste Bereich *Regelwerk* versprochen hatte.
        Er steht hier und nicht daneben: Wer den Unterschied liest und ihn
        nachsehen will, findet ihn eine Bildschirmhöhe weiter oben an
        derselben Stelle.
      */}
      {offen && vorher && (
        <Blattabschnitt
          kennung="handbuch.vergleich"
          titel={t('handbuch.abschnitt.vergleich', { nummer: String(vorher.ausgabe) })}
          zusatz={
            geaendert.length === 0
              ? t('handbuch.vergleich.zusatz.gleich')
              : t('handbuch.vergleich.zusatz', { anzahl: anzahlwort(geaendert.length) })
          }
          vorgabe={false}
        >
          {geaendert.length === 0 ? (
            <p className="erlaeuterung">{t('handbuch.vergleich.gleich')}</p>
          ) : (
            <div className="tabellenrahmen">
              <table ref={spaltenrahmen2} className="handbuchvergleich ziehbaretabelle">
                <Spaltenmassleiste masse={spaltenmasse2} />
                <thead>
                  <tr>
                    <th>{t('handbuch.vergleich.angabe')}</th>
                    <th>{t('handbuch.vergleich.vorher', { nummer: String(vorher.ausgabe) })}</th>
                    <th>{t('handbuch.vergleich.nachher', { nummer: String(offen.ausgabe) })}</th>
                  </tr>
                </thead>
                <tbody>
                  {geaendert.map((u) => (
                    <tr key={u.beschriftung}>
                      <td>{u.beschriftung}</td>
                      <td className="gedaempft">{u.vorher}</td>
                      <td className="betont">{u.nachher}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Blattabschnitt>
      )}

      {meldung && <p className="meldung">{meldung}</p>}

      {loeschen && darf('konzernhandbuchEntfernen') && (
        <Nachfrage
          titel={t('handbuch.entfernen.titel')}
          frage={t('handbuch.entfernen.frage', { nummer: String(loeschen.ausgabe), name: loeschen.name })}
          bestaetigen={t('handbuch.entfernen')}
          gefahr
          hinweis={t('handbuch.entfernen.hinweis')}
          aufJa={() => {
            const weg = loeschen.ausgabe
            void tue(() => api.konzernhandbuchEntfernen(weg), t('erfolg.handbuch.entfernt')).then(() =>
              setGewaehlt((bisher) => (bisher === weg ? null : bisher)),
            )
          }}
          aufSchliessen={() => setLoeschen(null)}
        />
      )}
    </section>
  )
}

/* ======================================================================
   Die Pflege einer Ausgabe
   ====================================================================== */

/**
 * Was eine Ausgabe trägt, solange sie auf dem Bildschirm steht.
 *
 * `gueltigVon` ist `null`, solange im Periodenfeld nichts Lesbares steht —
 * gespeichert wird dann nicht, statt eine `0` in die Datenbank zu schreiben.
 */
type Entwurf = {
  ausgabe: number
  name: string
  gueltigVon: number | null
  gueltigBis: number | null
  parameter: Regelparameter
}

/**
 * Drei Abschnitte: die Ausgabe, das Runden, die Konten (R515).
 *
 * **Jeder Abschnitt trägt zugeklappt seine Auskunft** — die Gültigkeit, die
 * Nachkommastellen, die Zahl der gesetzten Konten. Wer nur nachsehen will, was
 * gilt, muß nichts aufklappen.
 */
function Pflege({
  entwurf,
  konten,
  einheiten,
  gesperrt,
  laeuft,
  aufAenderung,
  aufSpeichern,
}: {
  entwurf: Entwurf
  konten: Auswahleintrag[]
  /** Die Gesellschaften — für die Schwellen je Paarbeziehung (R828). */
  einheiten: Auswahleintrag[]
  gesperrt: boolean
  laeuft: boolean
  aufAenderung: (e: Entwurf) => void
  aufSpeichern: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('konzernhandbuch:pflege')
  // Ohne Recht zum Pflegen ist kein Knopf da (R1180).
  const darf = useDarf()
  const darfPflegen = darf('konzernhandbuchPflegen')

  const p = entwurf.parameter

  function setzeP(teil: Partial<Regelparameter>) {
    aufAenderung({ ...entwurf, parameter: { ...p, ...teil } })
  }

  /** Die Schwellen je Paarbeziehung — nie `null`, damit die Tabelle rechnen kann. */
  const paargrenzen = p.icGrenzeJePaar ?? []

  function aenderePaar(stelle: number, teil: Partial<Icpaargrenze>) {
    setzeP({
      icGrenzeJePaar: paargrenzen.map((g, i) => (i === stelle ? { ...g, ...teil } : g)),
    })
  }

  const vollstaendig = entwurf.name.trim() !== '' && entwurf.gueltigVon !== null

  return (
    <div className="handbuchpflege">
      {/* ---- Die Ausgabe ---------------------------------------------- */}
      <Blattabschnitt
        kennung="handbuch.ausgabe"
        titel={t('handbuch.abschnitt.ausgabe')}
        zusatz={t('handbuch.zusatz.ausgabe', { nummer: String(entwurf.ausgabe) })}
      >
        <div className="stammdatenfelder">
          <label>
            <span className="name">{t('handbuch.feld.name')}</span>
            <input
              value={entwurf.name}
              disabled={gesperrt}
              onChange={(e) => aufAenderung({ ...entwurf, name: e.target.value })}
            />
            <span className="erlaeuterung">{t('handbuch.feld.name.hinweis')}</span>
          </label>

          <Periodenfeld
            klasse=""
            wert={entwurf.gueltigVon}
            gesperrt={gesperrt}
            beschriftung={t('handbuch.feld.gueltigVon')}
            erlaeuterung={t('handbuch.feld.gueltigVon.hinweis')}
            aufWert={(v) => aufAenderung({ ...entwurf, gueltigVon: v })}
          />

          <Periodenfeld
            klasse=""
            wert={entwurf.gueltigBis}
            gesperrt={gesperrt}
            beschriftung={t('handbuch.feld.gueltigBis')}
            erlaeuterung={t('handbuch.feld.gueltigBis.hinweis')}
            aufWert={(v) => aufAenderung({ ...entwurf, gueltigBis: v })}
          />
        </div>
      </Blattabschnitt>

      {/* ---- Das Runden ------------------------------------------------ */}
      <Blattabschnitt
        kennung="handbuch.rechnen"
        titel={t('handbuch.abschnitt.rechnen')}
        zusatz={t('handbuch.zusatz.rechnen', { stellen: anzahlwort(p.nachkommastellen) })}
      >
        <div className="stammdatenfelder">
          <label>
            <span className="name">{t('handbuch.feld.nachkommastellen')}</span>
            <input
              type="number"
              min={0}
              max={6}
              value={p.nachkommastellen}
              disabled={gesperrt}
              onChange={(e) => setzeP({ nachkommastellen: Number(e.target.value) })}
            />
            <span className="erlaeuterung">{t('handbuch.feld.nachkommastellen.hinweis')}</span>
          </label>

          {/*
            **Ein Betrag steht wie im Bericht** (R840 — Eine Zahl in den
            Stammdaten steht wie im Bericht): „10.000,00" und nicht „10000",
            getippt mit oder ohne Tausenderpunkt. Übernommen wird, was sich als
            Zahl liest; eine Fehleingabe sagt das Feld als Satz darunter.
          */}
          <Zahlenfeld
            klasse=""
            beschriftung={t('handbuch.feld.icGrenze')}
            darstellung="betrag"
            wert={String(p.icDifferenzGrenze)}
            gesperrt={gesperrt}
            aufWert={(zahl) => zahl !== null && setzeP({ icDifferenzGrenze: Number(zahl) })}
            erlaeuterung={t('handbuch.feld.icGrenze.hinweis')}
          />

          {/*
            **Die Nutzungsdauer ist Pflicht** (2.13 Abschnitt 17, R1396) —
            § 309 Abs. 1 HGB verlangt die Abschreibung. Bis zum 2026-10-07 hieß
            das leere Feld „wird nicht planmäßig abgeschrieben", und gewollt
            und vergessen sahen gleich aus. Gepflegt wird hier das Kapitel nach
            HGB; leer lässt es sich nicht speichern, und der Satz darunter sagt
            es, bevor jemand speichert.
          */}
          <label>
            <span className="name">{t('handbuch.feld.nutzungsdauer')}</span>
            <input
              type="number"
              min={0}
              value={p.goodwillNutzungsdauerMonate ?? ''}
              disabled={gesperrt}
              onChange={(e) =>
                setzeP({
                  goodwillNutzungsdauerMonate:
                    e.target.value.trim() === '' ? null : Number(e.target.value),
                })
              }
            />
            <span className="erlaeuterung">
              {p.goodwillNutzungsdauerMonate === null
                ? t('handbuch.feld.nutzungsdauer.ohne')
                : t('handbuch.feld.nutzungsdauer.hinweis', {
                    // Zehn Jahre und nicht "10.0": Die Zahl steht in einem Satz,
                    // und der Satz spricht die Sprache des Anwenders (R157) — mit dem
                    // Komma aus dem Zahlenformat der Fußleiste (R162): „7,5 Jahre".
                    jahre: new Intl.NumberFormat(zahlenschema(), {
                      maximumFractionDigits: 1,
                    }).format(p.goodwillNutzungsdauerMonate / 12),
                  })}
            </span>
          </label>
        </div>
      </Blattabschnitt>

      {/* ---- Die Schwellen je Paarbeziehung ---------------------------- */}
      {/*
        **Warum je Paar überhaupt** (R828, Auftrag vom 2026-09-27): *„FX-
        Gesellschaften haben immer kleinere Abweichungen, wohingegen reine
        EUR-Kombinationen sauber sein sollten."* Zwei Gesellschaften derselben
        Währung müssen auf den Cent stimmen; zwei mit verschiedenen tragen den
        umrechnungsbedingten Anteil immer mit sich. Eine Schwelle für beide
        wäre für die eine zu weit oder für die andere zu eng — und beides
        macht die Farbe im Abstimmblatt wertlos.

        **Ungerichtet**, wie das Pärchen im Abstimmblatt (R827): A↔B steht
        einmal da und gilt in beide Richtungen.
      */}
      <Blattabschnitt
        kennung="handbuch.icpaare"
        titel={t('handbuch.abschnitt.icpaare')}
        // Dass es keine gibt, ist eine Auskunft; wie viele es sind, keine
        // (7.2 Abschnitt 36, R1143).
        zusatz={paargrenzen.length === 0 ? t('handbuch.zusatz.icpaare.leer') : undefined}
      >
        {paargrenzen.length === 0 ? (
          <p className="erlaeuterung">{t('handbuch.icpaare.leer')}</p>
        ) : (
          <div className="tabellenrahmen">
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                <tr>
                  <th>{t('handbuch.icpaar.a')}</th>
                  <th>{t('handbuch.icpaar.b')}</th>
                  <th className="zahl">{t('handbuch.icpaar.grenze')}</th>
                  {darfPflegen && <th />}
                </tr>
              </thead>
              <tbody>
                {paargrenzen.map((g, i) => (
                  <tr key={i}>
                    <td>
                      <Auswahlfeld
                        wert={g.einheitA}
                        eintraege={einheiten}
                        abgeschaltet={gesperrt}
                        beschriftung={t('handbuch.icpaar.a')}
                        aufWahl={(einheitA) => aenderePaar(i, { einheitA })}
                      />
                    </td>
                    <td>
                      <Auswahlfeld
                        wert={g.einheitB}
                        eintraege={einheiten}
                        abgeschaltet={gesperrt}
                        beschriftung={t('handbuch.icpaar.b')}
                        aufWahl={(einheitB) => aenderePaar(i, { einheitB })}
                      />
                    </td>
                    <td className="zahl">
                      {/* Dasselbe Feld wie die allgemeine Grenze darüber (R840). */}
                      <Zahlenfeld
                        klasse="nurfeld"
                        beschriftung={t('handbuch.icpaar.grenze')}
                        darstellung="betrag"
                        wert={String(g.grenze)}
                        gesperrt={gesperrt}
                        aufWert={(zahl) => zahl !== null && aenderePaar(i, { grenze: Number(zahl) })}
                      />
                    </td>
                    {darfPflegen && (
                      <td className="handgriff">
                        <button
                          type="button"
                          className="schmal gefahr"
                          disabled={gesperrt || laeuft}
                          onClick={() =>
                            setzeP({ icGrenzeJePaar: paargrenzen.filter((_, x) => x !== i) })
                          }
                        >
                          {t('handbuch.icpaar.entfernen')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {darfPflegen && (
          <div className="knopfreihe">
            <button
              type="button"
              disabled={gesperrt || laeuft}
              onClick={() =>
                setzeP({
                  icGrenzeJePaar: [...paargrenzen, { einheitA: '', einheitB: '', grenze: 0 }],
                })
              }
            >
              {t('handbuch.icpaar.neu')}
            </button>
          </div>
        )}

        <p className="erlaeuterung">{t('handbuch.icpaare.hinweis')}</p>
      </Blattabschnitt>

      {/* ---- Die Konten ------------------------------------------------ */}
      <Blattabschnitt
        kennung="handbuch.konten"
        titel={t('handbuch.abschnitt.konten')}
      >
        <div className="stammdatenfelder">
          {KONTENFELDER.map((feld) => (
            <label key={feld}>
              <span className="name">{t(`handbuch.feld.${feld}`)}</span>
              <Kontofeld
                wert={p[feld]}
                eintraege={konten}
                abgeschaltet={gesperrt}
                beschriftung={t(`handbuch.feld.${feld}`)}
                aufWahl={(wert) => setzeP({ [feld]: wert } as Partial<Regelparameter>)}
              />
              <span className="erlaeuterung">{t(`handbuch.feld.${feld}.hinweis`)}</span>
            </label>
          ))}
        </div>
      </Blattabschnitt>

      {darfPflegen && (
        <div className="aktionsreihe">
          <button
            type="button"
            className="haupt"
            disabled={gesperrt || laeuft || !vollstaendig}
            onClick={aufSpeichern}
          >
            {t('handbuch.speichern')}
          </button>
        </div>
      )}
    </div>
  )
}

/* ======================================================================
   Die Handgriffe
   ====================================================================== */

/**
 * Die zehn Konten, die die Konsolidierung bebucht — in der Reihenfolge, in
 * der der Abschluß sie braucht. Die letzten beiden braucht erst ein Ereignis
 * nach dem Erwerb (R1540, R1555); sie dürfen leer bleiben.
 *
 * Sie stehen als Liste und nicht als zehn Felder im Text: Wer ein elftes
 * Konto hinzufügt, ergänzt hier eine Zeile und zwei Textbausteine, und der
 * Bildschirm zeigt es.
 */
const KONTENFELDER = [
  'firmenwertKonto',
  'unterschiedsbetragPassivKonto',
  'firmenwertAbschreibungKonto',
  'gewinnvortragKonto',
  'nichtBeherrschendKonto',
  'icDifferenzKonto',
  'icDifferenzKontoWaehrung',
  'zwischenergebnisKonto',
  'abschreibungFinanzanlagenKonto',
  'abgangBeteiligungenKonto',
] as const satisfies readonly (keyof Regelparameter)[]

/** Was eine neue Ausgabe mitbringt — dieselben Vorgaben wie im Rechenkern. */
const VORGABE: Regelparameter = {
  nachkommastellen: 2,
  icDifferenzGrenze: 10000,
  icDifferenzKonto: '',
  icDifferenzKontoWaehrung: '',
  firmenwertKonto: '',
  unterschiedsbetragPassivKonto: '',
  abschreibungFinanzanlagenKonto: '',
  abgangBeteiligungenKonto: '',
  nichtBeherrschendKonto: '',
  zwischenergebnisKonto: '',
  goodwillNutzungsdauerMonate: 120,
  firmenwertAbschreibungKonto: '',
  gewinnvortragKonto: '',
  // Leer heißt: Für alle gilt die allgemeine Grenze (R828).
  icGrenzeJePaar: null,
}

function ausAusgabe(a: Handbuchausgabe): Entwurf {
  return {
    ausgabe: a.ausgabe,
    name: a.name,
    gueltigVon: a.gueltigVon,
    gueltigBis: a.gueltigBis,
    parameter: { ...a.parameter },
  }
}

/** Von wann bis wann eine Ausgabe gilt — offen bleibt offen. */
export function geltung(a: { gueltigVon: number; gueltigBis: number | null }): string {
  return a.gueltigBis === null
    ? t('handbuch.geltung.offen', { von: periodenname(a.gueltigVon) })
    : t('handbuch.geltung.bis', {
        von: periodenname(a.gueltigVon),
        bis: periodenname(a.gueltigBis),
      })
}
